import Flex from 'components/ui/Flex';
import styled from 'styled-components';

import { clearErrors, clearSearchOrders } from 'store/reducers/orderSlice';
import { clearReturnSlip as clearReturnSlipSet } from 'store/reducers/returnSlice';
import { SignInSignOutButton } from 'components/ui/SignInSignOutButton';
import Text from 'components/ui/Text';
import { useLocation, useNavigate } from 'react-router-dom';
import useAppSelector from 'hooks/useAppSelector';

import { companiesSelector as companySelector } from 'store/reducers/companiesSlice';
import useAppDispatch from 'hooks/useAppDispatch';
import { ReturnCenterDisplaySettingsResponse } from 'returnCenter-api';
import { userSelector } from 'store/reducers/usersSlice';
import Spinner from 'components/ui/Spinner';
import { ContentContainer } from 'components/ui/ContentContainer';
import { isBatchRoute } from 'utils/navigation';
import { BATCH_RETURN_COLOR } from 'components/batch-returns/BatchReturn';

const Header = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const currentCompany = useAppSelector(companySelector.selectCurrentCompany);
    const siteSettings = useAppSelector(companySelector.selectDisplaySettings);
    const loadingSettings = useAppSelector(companySelector.selectAppSettingsLoading);
    const user = useAppSelector(userSelector.selectUserProfile);
    const loadingUser = useAppSelector(userSelector.selectUserProfileLoading);
    const location = useLocation();

    const batchRoute = isBatchRoute(location.pathname);

    const handleClickHome = () => {
        navigate(`/`);
        dispatch(clearErrors());
        dispatch(clearSearchOrders());
        dispatch(clearReturnSlipSet());
    };

    const renderLogoOrCompany = () => {
        if (loadingSettings) {
            return <Spinner color="text" />;
        }
        if (siteSettings?.logoUrl) {
            return (
                <StyledImage
                    className="cursor-pointer"
                    onClick={handleClickHome}
                    src={siteSettings.logoUrl}
                    alt="Company Logo"
                />
            );
        }
        return (
            <Text fontSize={20} as="span">
                {currentCompany?.name}
            </Text>
        );
    };

    const renderUserInfo = () => {
        if (loadingUser) {
            return <Spinner color="text" />;
        }
        return (
            <Text fontSize={16} as="span">
                {(user && user.displayName) || user?.userPrincipalName || 'User not signed in'}
            </Text>
        );
    };

    return (
        <Wrapper column $grow={1}>
            <ContentContainer column $grow={1}>
                <SiteContent $grow={1} align="center" justify="between" siteSettings={siteSettings ?? undefined}>
                    <ImageWrapper>{renderLogoOrCompany()}</ImageWrapper>
                    <StyledHeading
                        margin={0.5}
                        as="h1"
                        thin
                        fontSize={32}
                        siteSettings={siteSettings ?? undefined}
                        isBatch={batchRoute}
                    >
                        Return Center
                    </StyledHeading>
                    <Flex column align="center" gap="3px">
                        <SignInSignOutButton />
                        {renderUserInfo()}
                    </Flex>
                </SiteContent>
            </ContentContainer>
        </Wrapper>
    );
};
export default Header;

const Wrapper = styled(Flex)`
    padding: 0rem 1rem;
    background-color: ${({ theme }) => theme.colors.fillWhite};
`;

const SiteContent = styled(Flex)<{ siteSettings?: ReturnCenterDisplaySettingsResponse }>`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    height: 100px;
`;

const ImageWrapper = styled(Flex)`
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 60px;
`;

const StyledImage = styled('img')`
    max-height: 35px;
    width: 100%;
`;

const StyledHeading = styled(Text)<{ siteSettings?: ReturnCenterDisplaySettingsResponse; isBatch: boolean }>`
    color: ${({ siteSettings, theme, isBatch }) =>
        isBatch ? BATCH_RETURN_COLOR : siteSettings?.accentThemeColor || theme.colors.text};
`;
